"use client";
import Portal from "../_private/Portal/index.js";
import useIsDismissible from "../../hooks/_private/useIsDismissible.js";
import useHotkeys from "../../hooks/useHotkeys.js";
import useIsomorphicLayoutEffect from "../../hooks/useIsomorphicLayoutEffect.js";
import useScrollLock from "../../hooks/useScrollLock.js";
import useToggle from "../../hooks/useToggle.js";
import React from "react";
import { trapFocus } from "../../utilities/a11y.js";
import { onNextFrame } from "../../utilities/animation.js";
import { classNames } from "../../utilities/helpers.js";
import s from "./Overlay.module.css";
const Overlay = (props) => {
    const { active, children, transparent, onClose, className, attributes } = props;
    const clickThrough = transparent === true;
    const opacity = clickThrough ? 0 : (1 - (transparent || 0)) * 0.7;
    const [mounted, setMounted] = React.useState(false);
    const [animated, setAnimated] = React.useState(false);
    const contentRef = React.useRef(null);
    const isMouseDownValidRef = React.useRef(false);
    const releaseFocusRef = React.useRef(null);
    const { lockScroll, unlockScroll } = useScrollLock();
    const { active: rendered, activate: render, deactivate: remove } = useToggle(active || false);
    const { active: visible, activate: show, deactivate: hide } = useToggle(active || false);
    const isDismissible = useIsDismissible(active, contentRef);
    const rootClassNames = classNames(s.root, visible && s["--visible"], clickThrough && s["--click-through"], animated && s["--animated"], className);
    const isInsideChild = (el) => {
        if (!contentRef.current)
            return;
        const firstChild = contentRef.current.firstChild;
        if (!firstChild)
            return;
        return firstChild.contains(el);
    };
    const trapFocusInside = () => {
        if (!contentRef.current)
            return;
        releaseFocusRef.current = trapFocus(contentRef.current);
    };
    const cancelTrapFocus = () => {
        if (!releaseFocusRef.current)
            return;
        releaseFocusRef.current();
        releaseFocusRef.current = null;
    };
    const close = React.useCallback(() => {
        if (!visible || !isDismissible())
            return;
        if (onClose)
            onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, isDismissible]);
    const handleMouseDown = (event) => {
        isMouseDownValidRef.current = !isInsideChild(event.target);
    };
    const handleMouseUp = (event) => {
        const isMouseUpValid = !isInsideChild(event.target);
        const shouldClose = isMouseDownValidRef.current && isMouseUpValid && !clickThrough;
        if (!shouldClose)
            return;
        close();
    };
    const handleTransitionEnd = (e) => {
        if (e.propertyName !== "transform" || !e.pseudoElement)
            return;
        setAnimated(false);
        if (visible)
            return;
        if (!clickThrough)
            unlockScroll();
        remove();
    };
    useHotkeys({ Escape: close }, [close]);
    React.useEffect(() => {
        setAnimated(true);
        if (active && !rendered)
            render();
        if (!active && rendered)
            hide();
    }, [active, render, hide, rendered]);
    // Show overlay after it was rendered
    React.useEffect(() => {
        if (!rendered)
            return;
        if (!clickThrough)
            lockScroll();
        onNextFrame(() => {
            show();
        });
    }, [rendered, show, lockScroll, clickThrough]);
    React.useEffect(() => {
        if (!rendered)
            return;
        trapFocusInside();
        return () => cancelTrapFocus();
    }, [rendered]);
    // Unlock scroll on unmount
    React.useEffect(() => {
        return () => {
            if (!clickThrough)
                unlockScroll();
        };
    }, [unlockScroll, clickThrough]);
    useIsomorphicLayoutEffect(() => {
        setMounted(true);
    }, []);
    if (!rendered || !mounted)
        return null;
    return (React.createElement(Portal, null,
        React.createElement(Portal.Scope, null, (ref) => (React.createElement("div", { ...attributes, ref: ref, style: { "--rs-overlay-opacity": opacity }, role: "button", tabIndex: -1, className: rootClassNames, onMouseDown: handleMouseDown, onMouseUp: handleMouseUp, onTransitionEnd: handleTransitionEnd },
            React.createElement("div", { className: s.wrapper },
                React.createElement("div", { className: s.inner },
                    React.createElement("div", { className: s.content, ref: contentRef }, typeof children === "function" ? children({ active: visible }) : children))))))));
};
export default Overlay;
